import { getLocale, t, useI18n } from '../../i18n';
import React, { useState, useEffect, useRef } from 'react';
import {
  getReportShiftLabel,
  reportService,
} from '../../services/reportService';
import type {
  Report,
  ReportComment,
  ReportMedia,
  ReportStatusEvent,
  AdminProfile,
  ReportProfile,
} from '../../services/reportService';
import {
  buildReportSections,
  getReportTitle,
  reportTypeLabelFor,
} from '../../utils/reportPresentation';
import { exportReportToPdf, exportReportToDocx } from '../../utils/reportExport';
import { clientPortalAdminService } from '../../services/clientPortalAdminService';
import './ReportsPage.css';

interface StatusOption {
  value: string;
  label: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_review', label: 'In Review' },
  { value: 'needs_more_info', label: 'Needs More Info' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'closed', label: 'Closed' },
];

const getStatusLabel = (status: string | null | undefined): string => {
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  return opt ? opt.label : status || 'Unknown';
};

const getStatusBadgeClass = (status: string | null | undefined): string => {
  switch (status) {
    case 'submitted':
      return 'badge badge--status-submitted';
    case 'in_review':
      return 'badge badge--status-in-review';
    case 'needs_more_info':
      return 'badge badge--status-needs-info';
    case 'escalated':
      return 'badge badge--status-escalated';
    case 'closed':
      return 'badge badge--status-closed';
    default:
      return 'badge badge--default';
  }
};

const getPriorityBadgeClass = (priority: string | null | undefined): string => {
  switch (priority?.toLowerCase()) {
    case 'critical':
      return 'badge badge--critical';
    case 'high':
      return 'badge badge--high';
    case 'medium':
      return 'badge badge--medium';
    case 'low':
      return 'badge badge--low';
    default:
      return 'badge badge--default';
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return t('N/A');
  return new Date(dateStr).toLocaleString(getLocale());
};

const formatRelativeDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return t('just now');
  if (diffMins < 60) return t('{count}m ago', { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('{count}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t('{count}d ago', { count: diffDays });
  return date.toLocaleDateString(getLocale());
};

interface TimelineItem {
  type: 'status' | 'comment';
  date: string;
  data: ReportStatusEvent | ReportComment;
}

interface Props {
  reportId: string;
  onBack: () => void;
  /** Label for the back button — reflects where the report was opened from. */
  backLabel?: string;
  onReplayShift?: (shiftExecutionId: string) => void;
}

export default function ReportDetailPage({
  reportId,
  onBack,
  backLabel,
  onReplayShift,
}: Props) {
  useI18n();
  const [report, setReport] = useState<Report | null>(null);
  const [comments, setComments] = useState<ReportComment[]>([]);
  const [statusEvents, setStatusEvents] = useState<ReportStatusEvent[]>([]);
  const [media, setMedia] = useState<ReportMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState<ReportMedia | null>(null);
  const [currentUser, setCurrentUser] = useState<ReportProfile | null>(null);
  const [admins, setAdmins] = useState<AdminProfile[]>([]);
  const [companyName, setCompanyName] = useState('');

  // Comment form
  const [commentBody, setCommentBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Status change
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Assignment
  const [assigningTo, setAssigningTo] = useState('');
  const [updatingAssignment, setUpdatingAssignment] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<'comments' | 'timeline'>(
    'comments',
  );

  // Client release: only relevant when the site belongs to a client contract
  // whose portal holds this report back for review.
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState('');

  // Export
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const commentsEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const profile = await reportService.getCurrentUserProfile();
      setCurrentUser(profile);

      const [reportData, commentsData, eventsData, adminsData, fetchedCompanyName] =
        await Promise.all([
          reportService.fetchReportById(reportId),
          reportService.fetchComments(reportId),
          reportService.fetchStatusEvents(reportId),
          reportService.fetchAdmins(profile.company_id),
          reportService.fetchCompanyName(profile.company_id),
        ]);

      setReport(reportData);
      setComments(commentsData);
      setStatusEvents(eventsData);
      setAdmins(adminsData);
      setCompanyName(fetchedCompanyName);
      setAssigningTo((reportData?.assigned_to as string | null) ?? '');

      setMediaLoading(true);
      const mediaData = await reportService.fetchReportMedia(reportId);
      setMedia(mediaData);
      setMediaLoading(false);
    } catch (err) {
      console.error('Error loading report detail:', (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentBody.trim() || !currentUser) return;
    setSubmittingComment(true);
    try {
      const newComment = await reportService.addComment(
        reportId,
        currentUser.company_id,
        currentUser.id,
        commentBody.trim(),
        isInternal,
      );
      setComments((prev) => [...prev, newComment]);
      setCommentBody('');
      setIsInternal(false);
      setTimeout(
        () => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }),
        100,
      );
    } catch (err) {
      console.error('Error adding comment:', (err as Error).message);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await reportService.deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', (err as Error).message);
    }
  };

  const openStatusModal = () => {
    setNewStatus(report?.status ?? 'submitted');
    setStatusReason('');
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !report || newStatus === report.status) return;
    setUpdatingStatus(true);
    try {
      const updated = await reportService.updateReportStatus(
        reportId,
        newStatus,
        statusReason,
      );
      setReport((prev) => (prev ? { ...prev, ...updated } : prev));
      const eventsData = await reportService.fetchStatusEvents(reportId);
      setStatusEvents(eventsData);
      setShowStatusModal(false);
    } catch (err) {
      console.error('Error updating status:', (err as Error).message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssign = async (newAssigneeId: string) => {
    if (!currentUser || updatingAssignment) return;
    setUpdatingAssignment(true);
    try {
      const updated = await reportService.assignReport(
        reportId,
        newAssigneeId || null,
      );
      setReport((prev) =>
        prev
          ? {
              ...prev,
              assigned_to: updated.assigned_to,
              assignee: (updated as Record<string, unknown>)['assignee'],
            }
          : prev,
      );
      setAssigningTo((updated.assigned_to as string | null) ?? '');
      const eventsData = await reportService.fetchStatusEvents(reportId);
      setStatusEvents(eventsData);
    } catch (err) {
      console.error('Error updating assignment:', (err as Error).message);
    } finally {
      setUpdatingAssignment(false);
    }
  };

  const handleClientRelease = async (released: boolean) => {
    if (!report) return;
    setIsReleasing(true);
    setReleaseError('');
    try {
      const releasedAt = await clientPortalAdminService.setReportClientRelease(report.id, released);
      setReport((prev) => (prev ? { ...prev, client_released_at: releasedAt } : prev));
    } catch (err) {
      setReleaseError((err as Error).message);
    } finally {
      setIsReleasing(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    if (!report) return;
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      if (format === 'pdf') {
        await exportReportToPdf(report, comments, media, false, companyName);
      } else {
        await exportReportToDocx(report, comments, media, false, companyName);
      }
    } catch (err) {
      console.error('Export failed:', (err as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const backText = backLabel ?? t('Back to Reports');

  if (isLoading)
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );

  if (!report)
    return (
      <div className="page--full">
        <p className="text-muted">{t('Report not found.')}</p>
        <button
          onClick={onBack}
          className="btn btn--secondary"
          style={{ marginTop: 16 }}
        >
          {backText}
        </button>
      </div>
    );

  const isClosed = report.status === 'closed';

  const profiles = report['profiles'] as { full_name?: string | null } | null;
  const sites = report['sites'] as { name?: string | null } | null;
  const shiftLabel = getReportShiftLabel(report);
  const replayShiftExecutionId = report.shift_execution_id;
  const structuredSections = buildReportSections(report);

  const timeline: TimelineItem[] = [
    ...statusEvents.map((e) => ({
      type: 'status' as const,
      date: e.created_at,
      data: e,
    })),
    ...comments.map((c) => ({
      type: 'comment' as const,
      date: c.created_at,
      data: c,
    })),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <div className="page--full">
      {/* Header */}
      <div className="report-detail__header">
        <button onClick={onBack} className="btn btn--secondary">
          {backText}
        </button>
        <div className="report-detail__header-info">
          <h1 className="page__title">{getReportTitle(report)}</h1>
          <div className="report-detail__header-meta">
            <span className="badge badge--default">
              {reportTypeLabelFor(report.report_type)}
            </span>
            <span className={getStatusBadgeClass(report.status)}>
              {getStatusLabel(report.status)}
            </span>
            <span className={getPriorityBadgeClass(report.priority)}>
              {report.priority || 'N/A'}
            </span>
          </div>
        </div>
        <div className="report-detail__header-actions">
          <button onClick={openStatusModal} className="btn btn--primary">
            {isClosed ? 'Reopen' : 'Update Status'}
          </button>
          <div className="export-dropdown">
            <button
              className="btn btn--secondary"
              onClick={() => setShowExportMenu((v) => !v)}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting…' : 'Export ▾'}
            </button>
            {showExportMenu && (
              <div className="export-dropdown__menu">
                <button
                  className="export-dropdown__item"
                  onClick={() => void handleExport('pdf')}
                >
                  {t('Download PDF')}
                </button>
                <button
                  className="export-dropdown__item"
                  onClick={() => void handleExport('docx')}
                >
                  {t('Download Word (.docx)')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content layout */}
      <div className="report-detail__layout">
        {/* Left: report content + tabs */}
        <div className="report-detail__main">
          {/* Report content card */}
          <div className="card report-detail__content-card">
            {structuredSections.length > 0 && (
              <div className="report-structured">
                {structuredSections.map((section) => (
                  <section
                    key={section.title}
                    className="report-structured__section"
                  >
                    {section.facts.length === 0 && section.body ? (
                      <div className="report-structured__facts">
                        <div className="report-structured__fact">
                          <span>{section.title}</span>
                          <strong>{section.body}</strong>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="report-detail__label">
                          {section.title}
                        </span>
                        {section.facts.length > 0 && (
                          <div className="report-structured__facts">
                            {section.facts.map((fact) => (
                              <div
                                key={`${section.title}-${fact.label}`}
                                className="report-structured__fact"
                              >
                                <span>{fact.label}</span>
                                <strong>{fact.value}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                        {section.body && (
                          <p className="report-detail__value">{section.body}</p>
                        )}
                      </>
                    )}
                  </section>
                ))}
              </div>
            )}

            <div className="report-detail__field">
              <span className="report-detail__label">
                {structuredSections.length > 0 ? 'Source Summary' : 'Content'}
              </span>
              <p className="report-detail__value">
                {report.content || 'No content'}
              </p>
            </div>

            {/* Media */}
            {mediaLoading ? (
              <div className="report-media__loading">
                <div className="spinner spinner--sm" />
              </div>
            ) : (
              media.length > 0 && (
                <div className="report-media">
                  <span className="report-detail__label">{t('Attachments')}</span>
                  <div className="report-media__grid">
                    {media.map((m) => (
                      <div
                        key={m.id}
                        className="report-media__item"
                        onClick={() => setLightboxMedia(m)}
                      >
                        {m.media_type === 'video' ? (
                          <video
                            src={m.url ?? ''}
                            className="report-media__thumb"
                            muted
                          />
                        ) : (
                          <img
                            src={m.url ?? ''}
                            alt={t('Attachment')}
                            className="report-media__thumb"
                          />
                        )}
                        <span className="report-media__type-badge">
                          {m.media_type === 'video' ? 'Video' : 'Photo'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* Tabs: Comments / Timeline */}
          <div className="report-detail__tabs">
            <button
              className={`report-detail__tab ${activeTab === 'comments' ? 'report-detail__tab--active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              Comments ({comments.length})
            </button>
            <button
              className={`report-detail__tab ${activeTab === 'timeline' ? 'report-detail__tab--active' : ''}`}
              onClick={() => setActiveTab('timeline')}
            >
              Timeline ({timeline.length})
            </button>
          </div>

          {activeTab === 'comments' && (
            <div className="report-detail__comments">
              {comments.length === 0 ? (
                <p className="text-muted" style={{ padding: '16px 0' }}>
                  {t('No comments yet')}
                </p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`report-comment ${comment.is_internal ? 'report-comment--internal' : ''}`}
                  >
                    <div className="report-comment__header">
                      <div className="report-comment__author">
                        <span className="report-comment__name">
                          {comment.profiles?.full_name || 'Unknown'}
                        </span>
                        {comment.profiles?.role === 'admin' && (
                          <span
                            className="badge badge--status-in-review"
                            style={{ fontSize: 10, padding: '1px 6px' }}
                          >
                            {t('Admin')}
                          </span>
                        )}
                        {comment.is_internal && (
                          <span
                            className="badge badge--status-escalated"
                            style={{ fontSize: 10, padding: '1px 6px' }}
                          >
                            {t('Internal')}
                          </span>
                        )}
                      </div>
                      <div className="report-comment__meta">
                        <span
                          className="text-faint"
                          title={formatDate(comment.created_at)}
                        >
                          {formatRelativeDate(comment.created_at)}
                        </span>
                        {currentUser &&
                          comment.author_id === currentUser.id && (
                            <button
                              className="btn btn--icon report-comment__delete"
                              onClick={() => handleDeleteComment(comment.id)}
                              title={t('Delete comment')}
                            >
                              ✕
                            </button>
                          )}
                      </div>
                    </div>
                    <p className="report-comment__body">{comment.body}</p>
                  </div>
                ))
              )}
              <div ref={commentsEndRef} />

              {/* Add comment form */}
              <form
                className="report-comment__form"
                onSubmit={handleAddComment}
              >
                <textarea
                  className="form__textarea"
                  placeholder={t('Write a comment...')}
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  rows={3}
                />
                <div className="report-comment__form-actions">
                  <label className="report-comment__internal-toggle">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                    />
                    <span className="text-faint">
                      {t('Internal note (not visible to guard)')}
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={!commentBody.trim() || submittingComment}
                  >
                    {submittingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="report-detail__timeline">
              {timeline.length === 0 ? (
                <p className="text-muted" style={{ padding: '16px 0' }}>
                  {t('No activity yet')}
                </p>
              ) : (
                timeline.map((item, idx) => {
                  const isStatusEvent = item.type === 'status';
                  const statusData = isStatusEvent
                    ? (item.data as ReportStatusEvent)
                    : null;
                  const commentData = !isStatusEvent
                    ? (item.data as ReportComment)
                    : null;
                  const isAssignment =
                    isStatusEvent &&
                    (statusData as Record<string, unknown>)['event_type'] ===
                      'assignment_change';

                  return (
                    <div key={idx} className="timeline-item">
                      <div className="timeline-item__indicator">
                        <div
                          className={`timeline-item__dot ${isAssignment ? 'timeline-item__dot--assignment' : isStatusEvent ? 'timeline-item__dot--status' : 'timeline-item__dot--comment'}`}
                        />
                        {idx < timeline.length - 1 && (
                          <div className="timeline-item__line" />
                        )}
                      </div>
                      <div className="timeline-item__content">
                        {isAssignment && statusData ? (
                          <>
                            <div className="timeline-item__header">
                              <span className="text-label">
                                {statusData.profiles?.full_name || 'System'}
                              </span>
                              <span className="text-muted">
                                {' '}
                                {
                                  (statusData as Record<string, unknown>)[
                                    'reason'
                                  ] as string
                                }
                              </span>
                            </div>
                          </>
                        ) : isStatusEvent && statusData ? (
                          <>
                            <div className="timeline-item__header">
                              <span className="text-label">
                                {statusData.profiles?.full_name || 'System'}
                              </span>
                              <span className="text-muted">
                                {' '}
                                changed status{' '}
                              </span>
                              {Boolean(
                                (statusData as Record<string, unknown>)[
                                  'from_status'
                                ],
                              ) && (
                                <>
                                  <span
                                    className={getStatusBadgeClass(
                                      (statusData as Record<string, unknown>)[
                                        'from_status'
                                      ] as string,
                                    )}
                                    style={{ fontSize: 10 }}
                                  >
                                    {getStatusLabel(
                                      (statusData as Record<string, unknown>)[
                                        'from_status'
                                      ] as string,
                                    )}
                                  </span>
                                  <span className="text-muted"> &rarr; </span>
                                </>
                              )}
                              <span
                                className={getStatusBadgeClass(
                                  (statusData as Record<string, unknown>)[
                                    'to_status'
                                  ] as string,
                                )}
                                style={{ fontSize: 10 }}
                              >
                                {getStatusLabel(
                                  (statusData as Record<string, unknown>)[
                                    'to_status'
                                  ] as string,
                                )}
                              </span>
                            </div>
                            {(statusData as Record<string, unknown>)[
                              'reason'
                            ] && (
                              <p className="timeline-item__reason text-muted">
                                {
                                  (statusData as Record<string, unknown>)[
                                    'reason'
                                  ] as string
                                }
                              </p>
                            )}
                          </>
                        ) : commentData ? (
                          <>
                            <div className="timeline-item__header">
                              <span className="text-label">
                                {commentData.profiles?.full_name || 'Unknown'}
                              </span>
                              <span className="text-muted"> {t('commented')}</span>
                              {commentData.is_internal && (
                                <span
                                  className="badge badge--status-escalated"
                                  style={{
                                    fontSize: 10,
                                    padding: '1px 6px',
                                    marginLeft: 6,
                                  }}
                                >
                                  {t('Internal')}
                                </span>
                              )}
                            </div>
                            <p className="timeline-item__body">
                              {commentData.body}
                            </p>
                          </>
                        ) : null}
                        <span
                          className="text-faint"
                          style={{ fontSize: 11 }}
                          title={formatDate(item.date)}
                        >
                          {formatRelativeDate(item.date)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Sidebar: metadata */}
        <div className="report-detail__sidebar">
          <div className="card">
            <h3
              className="section__title"
              style={{ marginBottom: 16, fontSize: 14 }}
            >
              {t('Details')}
            </h3>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Guard')}</span>
              <span className="text-label">
                {profiles?.full_name || 'Unknown'}
              </span>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Site')}</span>
              <span className="text-label">{sites?.name || 'Unknown'}</span>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Shift')}</span>
              <span className="text-label">{shiftLabel}</span>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Type')}</span>
              <span className="text-label">
                {reportTypeLabelFor(report.report_type)}
              </span>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Priority')}</span>
              <span className={getPriorityBadgeClass(report.priority)}>
                {report.priority || 'N/A'}
              </span>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Status')}</span>
              <span className={getStatusBadgeClass(report.status)}>
                {getStatusLabel(report.status)}
              </span>
            </div>
            <div
              className="report-detail__sidebar-field"
              style={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <span className="report-detail__label">{t('Client portal')}</span>
              <span className="text-label">
                {report.client_released_at
                  ? t('Visible to the client since {date}', {
                      date: new Date(report.client_released_at as string).toLocaleDateString(getLocale()),
                    })
                  : t('Not released to the client')}
              </span>
              <button
                type="button"
                className={report.client_released_at ? 'btn btn--secondary btn--sm' : 'btn btn--primary btn--sm'}
                disabled={isReleasing}
                onClick={() => void handleClientRelease(!report.client_released_at)}
              >
                {isReleasing
                  ? t('Saving…')
                  : report.client_released_at
                    ? t('Withhold from client')
                    : t('Release to client')}
              </button>
              {releaseError ? (
                <span className="report-detail__label" style={{ color: 'var(--color-error-light)' }}>
                  {releaseError}
                </span>
              ) : null}
            </div>
            <div
              className="report-detail__sidebar-field"
              style={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
              }}
            >
              <span className="report-detail__label">{t('Assigned To')}</span>
              <select
                className="form__select"
                style={{ width: '100%', fontSize: 13 }}
                value={assigningTo}
                onChange={(e) => handleAssign(e.target.value)}
                disabled={updatingAssignment}
              >
                <option value="">{t('Unassigned')}</option>
                {admins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="report-detail__sidebar-field">
              <span className="report-detail__label">{t('Created')}</span>
              <span className="text-muted">
                {formatDate(report.created_at)}
              </span>
            </div>
            {report.status_updated_at &&
              report.status_updated_at !== report.created_at && (
                <div className="report-detail__sidebar-field">
                  <span className="report-detail__label">{t('Last Updated')}</span>
                  <span className="text-muted">
                    {formatDate(report.status_updated_at)}
                  </span>
                </div>
              )}
          </div>

          {/* Quick actions */}
          <div className="card" style={{ marginTop: 12 }}>
            <h3
              className="section__title"
              style={{ marginBottom: 12, fontSize: 14 }}
            >
              {t('Actions')}
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <button
                className="btn btn--secondary btn--full-width"
                onClick={() =>
                  replayShiftExecutionId &&
                  onReplayShift?.(replayShiftExecutionId)
                }
                disabled={!replayShiftExecutionId}
              >
                {replayShiftExecutionId ? 'Replay Shift' : 'Replay Unavailable'}
              </button>
            </div>
            {!isClosed ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {report.status === 'submitted' && (
                  <button
                    className="btn btn--primary btn--full-width"
                    onClick={async () => {
                      try {
                        const updated = await reportService.updateReportStatus(
                          reportId,
                          'in_review',
                        );
                        setReport((prev) =>
                          prev ? { ...prev, ...updated } : prev,
                        );
                        const eventsData =
                          await reportService.fetchStatusEvents(reportId);
                        setStatusEvents(eventsData);
                      } catch (err) {
                        console.error(
                          'Error starting review:',
                          (err as Error).message,
                        );
                      }
                    }}
                  >
                    {t('Start Review')}
                  </button>
                )}
                <button
                  className="btn btn--secondary btn--full-width"
                  onClick={openStatusModal}
                >
                  {t('Change Status')}
                </button>
                <button
                  className="btn btn--danger btn--full-width"
                  onClick={async () => {
                    try {
                      const updated = await reportService.updateReportStatus(
                        reportId,
                        'closed',
                        'Resolved',
                      );
                      setReport((prev) =>
                        prev ? { ...prev, ...updated } : prev,
                      );
                      const eventsData =
                        await reportService.fetchStatusEvents(reportId);
                      setStatusEvents(eventsData);
                    } catch (err) {
                      console.error(
                        'Error closing report:',
                        (err as Error).message,
                      );
                    }
                  }}
                >
                  {t('Close Report')}
                </button>
              </div>
            ) : (
              <button
                className="btn btn--primary btn--full-width"
                onClick={async () => {
                  try {
                    const updated = await reportService.updateReportStatus(
                      reportId,
                      'submitted',
                      'Reopened',
                    );
                    setReport((prev) =>
                      prev ? { ...prev, ...updated } : prev,
                    );
                    const eventsData =
                      await reportService.fetchStatusEvents(reportId);
                    setStatusEvents(eventsData);
                  } catch (err) {
                    console.error(
                      'Error reopening report:',
                      (err as Error).message,
                    );
                  }
                }}
              >
                {t('Reopen Report')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxMedia && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxMedia(null)}
        >
          <button
            className="lightbox__close btn btn--icon"
            onClick={() => setLightboxMedia(null)}
          >
            ✕
          </button>
          <div
            className="lightbox__content"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxMedia.media_type === 'video' ? (
              <video
                src={lightboxMedia.url ?? ''}
                controls
                autoPlay
                className="lightbox__media"
              />
            ) : (
              <img
                src={lightboxMedia.url ?? ''}
                alt={t('Attachment')}
                className="lightbox__media"
              />
            )}
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal modal--sm">
            <div className="modal__header">
              <h2 className="modal__title">{t('Update Status')}</h2>
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn--icon"
              >
                ✕
              </button>
            </div>
            <div className="form__group">
              <label className="form__label">{t('New Status')}</label>
              <select
                className="form__select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form__group">
              <label className="form__label">{t('Reason (optional)')}</label>
              <textarea
                className="form__textarea"
                placeholder={t('Add a reason for this status change...')}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={3}
              />
            </div>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn--secondary"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleUpdateStatus}
                className="btn btn--primary"
                disabled={
                  !newStatus || newStatus === report.status || updatingStatus
                }
              >
                {updatingStatus ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
